import { Box, Typography, Paper, Stack } from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import { memo, useEffect, useMemo, useState } from "react"

import { OrderRow } from "./OrderRow"
import { SingleHopperAgent } from "./SingleHopperAgent"
import { getActiveOrders, getHopperAgent } from "@/api/agents-api"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { InMemoryTable } from "@/components/InMemoryTable"
import { LoadingSkeletons } from "@/components/LoadingSkeletons"
import { NumberFont } from "@/components/RootLayout/fonts"
import { UsdAmountBlock } from "@/components/UsdAmountBlock"

function EntityAgentsBase(props: {
  entityId: string
  onAgentsValue?: (value: number | null) => void
}) {
  const { entityId, onAgentsValue } = props

  // multi hop agent
  const { data: hopperAgentId } = useQuery({
    queryKey: [`hopper_agent`, entityId],
    enabled: !!entityId,
    queryFn: async () => {
      if (!entityId) throw new Error("Params missing")

      const result = await getHopperAgent(entityId)
      console.log("onHopperAgent", result)
      return result
    },
  })

  const { data: orders } = useQuery({
    queryKey: [`active_orders`, entityId],
    enabled: !!entityId,
    queryFn: async () => {
      if (!entityId) throw new Error("Params missing")

      const result = await getActiveOrders(entityId)
      console.log("onActiveOrders", result)
      return result
    },
  })

  const [values, setValues] = useState<Record<string, number>>({})

  const totalValue = useMemo(() => {
    if (!orders) return null
    const val = Object.values(values).reduce((acc, value) => acc + value, 0)

    onAgentsValue?.(val)
    return val
  }, [orders, values])

  useEffect(() => {
    setValues({})
  }, [entityId])

  return (
    <Box>
      <Typography
        sx={{
          marginTop: 2,
          marginBottom: 1,
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
        }}
        variant="h4"
      >
        Botega Agents{" "}
        {!!totalValue && (
          <>
            •{" "}
            <Typography fontFamily={NumberFont} variant="inherit" component="span">
              <UsdAmountBlock amount={totalValue} />
            </Typography>
          </>
        )}
      </Typography>
      <Stack gap={2}>
        {hopperAgentId && (
          <Paper sx={{ border: 0, paddingX: 2, paddingY: 2 }}>
            <SingleHopperAgent entityId={hopperAgentId} />
          </Paper>
        )}
        {!orders ? (
          <LoadingSkeletons />
        ) : (
          <Paper sx={{ border: 0, paddingX: 2, paddingY: 2 }}>
            {orders.length === 0 ? (
              <Typography sx={{ marginX: 2, marginY: 1 }} variant="body2" color="text.secondary">
                No agents found.
              </Typography>
            ) : (
              <InMemoryTable
                initialSortField="netWorth"
                initialSortDir="desc"
                data={orders}
                headerCells={[
                  { label: "#", align: "right", sx: { width: 50 } },
                  { label: "Agent" },
                  { label: "Transactions", align: "right" },
                  { label: "24h Vol", align: "right" },
                  { label: "Total Value", align: "right" },
                ]}
                renderRow={(order, index) => (
                  <ErrorBoundary key={order.id} fallback={<>Something went wrong</>}>
                    <OrderRow
                      order={order}
                      index={index}
                      onAgentValue={(val) => {
                        setValues((prev) => ({ ...prev, [order.id]: val }))
                      }}
                    />
                  </ErrorBoundary>
                )}
              />
            )}
          </Paper>
        )}
      </Stack>
    </Box>
  )
}

export const EntityAgents = memo(EntityAgentsBase)
